// Tryb — the studio shell.
//
// A fixed rail, a header that always says whether this facilitator is verified,
// and whichever page is selected. Starting a listing is gated on verification:
// an unverified host is sent to the verification page rather than into a wizard
// they could not publish from.

import { Badge, Avatar, Button, Icon } from "../ds/index.js";
import Brand from "../components/Brand.jsx";
import { Overline } from "../components/Card.jsx";
import { useStudio } from "../lib/studioContext.js";
import { certificationProgress } from "../lib/studioView.js";
import Home from "./pages/Home.jsx";
import MyTrybs from "./pages/MyTrybs.jsx";
import Venues from "./pages/Venues.jsx";
import Certification from "./pages/Certification.jsx";
import Verification from "./pages/Verification.jsx";
import Account from "./pages/Account.jsx";
import SeasonReup from "./pages/SeasonReup.jsx";

/**
 * Publishing is gated on identity, and a facilitator can skip verification
 * during onboarding — so the requirement has to stay on screen rather than
 * living in a toast they'll miss.
 */
function VerifyFirstBanner({ status, onVerify }) {
  const pending = status === "Pending";
  return (
    <div
      style={{
        background: pending ? "var(--honey-100)" : "var(--ember-100)",
        border: `1px solid ${pending ? "var(--honey-300)" : "var(--ember-300)"}`,
        borderRadius: "var(--radius-xl)",
        padding: "18px 22px",
        display: "flex",
        alignItems: "center",
        gap: 16,
      }}
    >
      <span
        style={{
          width: 40, height: 40, borderRadius: "50%", flex: "none",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: pending ? "var(--honey-500)" : "var(--ember-500)",
          color: pending ? "var(--ink-900)" : "var(--sand-50)",
        }}
      >
        <Icon name={pending ? "Clock" : "Check"} size={20} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "var(--text-md)" }}>
          {pending ? "A person is checking your ID." : "Verify your identity to publish a tryb."}
        </div>
        <div style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", marginTop: 2, lineHeight: 1.5 }}>
          {pending
            ? "Usually within a day. Draft away meanwhile — publishing unlocks when it clears."
            : "You skipped this during setup. Everything else in the studio works, but nothing goes in front of members until it’s done."}
        </div>
      </div>
      <Button variant="primary" onClick={onVerify}>
        {pending ? "Speed it up" : "Verify now"}
      </Button>
    </div>
  );
}

const PAGES = [
  ["home", "Home", "Grid"],
  ["trybs", "Trybs", "Calendar"],
  ["venues", "Venues", "Pin"],
  ["cert", "Certification", "Star"],
  ["kyc", "Verification", "Check"],
  ["account", "Account", "User"],
];

const TITLES = {
  home: "Home",
  trybs: "Trybs",
  venues: "Venues",
  cert: "Certification",
  kyc: "Verification",
  account: "Account & payout",
  reup: "Season 2 · confirm and publish",
};

export default function Dashboard({ page, onNavigate, onStartListing, onStartVenue, onEditListing, onOpenCover, account, onSignOut }) {
  const { vals, state, venues, trybs, verifyStatus, showToast } = useStudio();

  const hostName = vals["A1.02"] || "New facilitator";
  const cert = certificationProgress(vals);

  const verifyTone = { Verified: "success", Pending: "warning", Unverified: "neutral" }[verifyStatus];
  const verifyBadge = verifyStatus === "Verified" ? "Verified" : verifyStatus === "Pending" ? "ID under review" : "Not verified";
  const verifyText =
    verifyStatus === "Verified" ? "Verified facilitator" : verifyStatus === "Pending" ? "ID being checked" : "Not verified yet";

  /** Publishing needs a verified identity — everything else is open. */
  const gated = (fn) => () => {
    if (verifyStatus !== "Verified") {
      onNavigate("kyc");
      showToast(
        "Verify yourself first",
        verifyStatus === "Pending"
          ? "Your document is with our team. We’ll have it checked within a day."
          : "Send us one document. Our team checks it, then you can publish.",
      );
      return;
    }
    fn();
  };

  const countFor = (id) =>
    id === "trybs"
      ? trybs.length || ""
      : id === "venues"
        ? venues.length || ""
        : id === "cert"
          ? `${cert.done}/31`
          : id === "kyc"
            ? verifyStatus === "Verified"
              ? ""
              : verifyStatus === "Pending"
                ? "review"
                : "to do"
            : "";

  const pageProps = {
    onNavigate,
    onStartListing: gated(onStartListing),
    onStartVenue,
    onEditListing: gated(onEditListing),
    onOpenCover,
  };

  return (
    <div data-screen-label="Dashboard" style={{ display: "flex", width: "100%", minHeight: "100vh" }}>
      <aside
        className="tryb-rail"
        style={{
          width: 248,
          flex: "none",
          background: "var(--ink-800)",
          color: "var(--sand-75)",
          padding: "22px 16px",
          position: "sticky",
          top: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ padding: "0 8px 22px" }}>
          <Brand size={60} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
          {PAGES.map(([id, label, icon]) => {
            const on = page === id;
            const count = countFor(id);
            return (
              <button
                key={id}
                onClick={() => onNavigate(id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  padding: "11px 12px",
                  borderRadius: "var(--radius-md)",
                  fontFamily: "var(--font-body)",
                  fontSize: 15,
                  fontWeight: on ? 700 : 500,
                  background: on ? "var(--sage-600)" : "transparent",
                  color: on ? "var(--sand-50)" : "color-mix(in srgb, var(--sand-75) 78%, transparent)",
                  transition: "background var(--dur-base) var(--ease-standard)",
                }}
              >
                <Icon name={icon} size={20} />
                <span style={{ flex: 1 }}>{label}</span>
                {count !== "" && <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, opacity: 0.8 }}>{count}</span>}
              </button>
            );
          })}
        </div>

        <div
          style={{
            borderTop: "1px solid color-mix(in srgb, var(--sand-75) 16%, transparent)",
            paddingTop: 14,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <Avatar name={hostName} size="md" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{hostName}</div>
            <div style={{ fontSize: 11, opacity: 0.65 }}>{account?.maskedPhone || verifyText}</div>
          </div>
          {onSignOut && (
            <button
              type="button"
              onClick={onSignOut}
              title="Sign out"
              aria-label="Sign out"
              style={{
                border: "none", background: "transparent", cursor: "pointer", padding: 4,
                color: "color-mix(in srgb, var(--sand-75) 70%, transparent)", display: "inline-flex", flex: "none",
              }}
            >
              <Icon name="LogOut" size={18} />
            </button>
          )}
        </div>
      </aside>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 32px",
            borderBottom: "1px solid var(--border-subtle)",
            background: "var(--surface-page)",
            position: "sticky",
            top: 0,
            zIndex: 5,
          }}
        >
          <div>
            <Overline color="var(--ember-700)">Facilitator studio</Overline>
            <h1 style={{ margin: "2px 0 0", fontFamily: "var(--font-display)", fontSize: "var(--text-2xl)", fontWeight: 800, letterSpacing: "var(--tracking-tight)" }}>
              {TITLES[page]}
            </h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Badge tone={verifyTone} dot>
              {verifyBadge}
            </Badge>
            <Button variant="accent" onClick={pageProps.onStartListing}>
              New tryb
            </Button>
          </div>
        </header>

        <div style={{ flex: 1, padding: 32, display: "flex", flexDirection: "column", gap: 20, maxWidth: 1180, width: "100%" }}>
          {verifyStatus !== "Verified" && page !== "kyc" && (
            <VerifyFirstBanner status={verifyStatus} onVerify={() => onNavigate("kyc")} />
          )}
          {page === "home" && <Home {...pageProps} />}
          {page === "trybs" && <MyTrybs {...pageProps} />}
          {page === "venues" && <Venues {...pageProps} />}
          {page === "cert" && <Certification />}
          {page === "kyc" && <Verification />}
          {page === "account" && <Account />}
          {page === "reup" && <SeasonReup {...pageProps} />}
        </div>
      </div>
    </div>
  );
}
